import React, { useContext, useEffect, useState, useRef } from 'react';
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout';
import { Dialog } from '@progress/kendo-react-dialogs';
import { DropDownList } from '@progress/kendo-react-dropdowns';
import { useHistory } from 'react-router-dom';
import { LocalizationContext } from '../../contexts';
import { usePage } from '../../hooks';
import { nairobiSite } from '../../utils/fetch';
import { pages, languageWorkspaceOptions } from '../../config';
import { getLangAndWorkspace } from '../../utils/helper';
import UpdateController from '../../components/UpdateController';
import Loader from '../../components/Loader';
import PageComponent from '../../components/PageComponent';
import './index.scss';

export default function PageManagement({ match }) {
    const history = useHistory();
    const [localization] = useContext(LocalizationContext);
    const { labels, ui_language } = localization;
    const { page, pageLang, section } = match.params;
    const [contentSelect, setContentSelect] = useState(-1);
    const [sectionSelect, setSectionSelect] = useState(-1);
    const [pageConfig, setPageConfig] = useState(null);
    const [updateData, setUpdateData] = useState(false);
    const [emptySectionData, setEmptySectionData] = useState({});
    const [updateError, setUpdateError] = useState(false);
    const validatorRef = useRef(null);
    const {
        isLoadingPage,
        errorLoadingPage,
        pageContent,
        pageData,
        getPage,
        setPage,
        clearErrorMessageLoading
    } = usePage();

    // Get valid combo IDs for URL validation
    const validComboIds = languageWorkspaceOptions.map(opt => opt.id);

    useEffect(() => {
        let contentSelect = section === 'seo' ? 1 : 0;
        let sectionSelect;
        if (contentSelect || !pageConfig || pageConfig.content.filter(p => p.key).length === 0) {
            sectionSelect = -1;
        }
        else if (!section) {
            sectionSelect = pageConfig.content.findIndex(p => p.key);
        }
        else {
            sectionSelect = pageConfig.content.findIndex(p => p.key === section);
        }

        setUpdateData(false);
        setContentSelect(contentSelect);
        setSectionSelect(sectionSelect);
    }, [section, pageConfig]);

    useEffect(() => {
        setUpdateData(false);
        // Validate pageLang is a valid combo ID
        if (!pageLang || validComboIds.indexOf(pageLang) < 0) {
            setTimeout(() => {
                // Default to first combo option
                history.push(`/page/${page}/${languageWorkspaceOptions[0].id}`);
            }, 1);
        }
        else {
            let pageConfig = pages.find(p => p.key === page);
            setPageConfig(pageConfig);
            loadPage();
        }
    }, [page, pageLang]);

    useEffect(() => {
        if (pageData && pageContent && pageData.comboId !== pageLang) {
            loadPage();
        }
        setEmptySectionData({});
    }, [pageContent]);

    const loadPage = () => {
        if (page && pageLang) {
            setUpdateData(false);
            // Extract lang and workspace from combo ID
            const { lang, workspace } = getLangAndWorkspace(pageLang);
            getPage(page, lang, workspace, pageLang);
        }
    };

    const handleContentSelect = e => {
        if (contentSelect !== e.selected) {
            let section = 'seo';
            if (e.selected === 0) {
                let page = pageConfig.content.filter(p => p.key)[0];
                section = page?.key || '';
            }
            history.push(`/page/${page}/${pageLang || languageWorkspaceOptions[0].id}/${section}`);
        }
    };

    const handleSectionSelect = e => {
        if (sectionSelect !== e.selected && e.selected < pageConfig.content.length && pageConfig.content[e.selected].key) {
            history.push(`/page/${page}/${pageLang || languageWorkspaceOptions[0].id}/${pageConfig.content[e.selected].key}`);
        }
    };

    const handleUpdateData = (updateData) => {
        setUpdateData(updateData);
    }

    const update = () => {
        const savedSection = section ? section : pageConfig.content.filter(p => p.key)[0].key;
        let newContent = { ...pageContent };
        newContent[savedSection] = { ...updateData };

        const fetch = new Promise((resolve, reject) => {
            const rejectFun = (error) => {
                var errorMessage = error && typeof (error) === 'object' && 'customErrorMessage' in error && error.customErrorMessage !== false ? error.customErrorMessage : labels.save_error_message;
                if (errorMessage) {
                    setUpdateError(errorMessage);
                }
                reject({ dontShowErrorMessage: true });
            };

            const resolveFun = (data) => {
                setUpdateData(false);
                resolve(data);
            };

            if (validatorRef && validatorRef.current) {
                let isValid = validatorRef.current();
                if (isValid !== true) {
                    rejectFun({ customErrorMessage: isValid });
                    return;
                }
            }

            setPage(savedSection, newContent).then(data => {
                resolveFun(data);
            }).catch(error => {
                rejectFun(error);
            });
        });
        return fetch;
    };

    const formatDate = (inputDate) => {
        let date = '';
        if (inputDate) {
            date = typeof (inputDate) === 'string' ? new Date(inputDate) : inputDate;
            let year = date.getFullYear();
            let month = date.getMonth() + 1;
            month = month < 10 ? '0' + month : month;
            let day = date.getDate();
            let hours = date.getHours();
            let minutes = date.getMinutes();
            minutes = minutes < 10 ? '0' + minutes : minutes;
            date = day + '/' + month + '/' + year + ' ' + hours + ':' + minutes;
        }
        return date;
    };

    // Build languages list from languageWorkspaceOptions
    const languagesList = languageWorkspaceOptions.map(option => ({
        text: labels[option.labelKey],
        id: option.id
    }));

    // Get the actual lang for URL display (extract from combo)
    const { lang: displayLang, workspace } = getLangAndWorkspace(pageLang || languageWorkspaceOptions[0].id);
    const pageUrl = `${nairobiSite}/${workspace==='ivritil' ? 'ivritil' : displayLang}/${page === 'home' ? '' : page}`;
    const _section = (pageConfig && pageConfig.seoOnly) ? "seo" : section ? section : pageConfig && pageConfig.content.filter(p => p.key).length > 0 ? pageConfig.content.filter(p => p.key)[0].key : '';
    const compType = contentSelect || (pageConfig && pageConfig.seoOnly) ? 'seo' : sectionSelect >= 0 ? pageConfig.content[sectionSelect].type : '';
    const settingsData = contentSelect || (pageConfig && pageConfig.seoOnly) ? {} : sectionSelect >= 0 ? pageConfig.content[sectionSelect].settingsData : {};
    const sectionData = pageContent && pageContent[_section] ? pageContent[_section] : emptySectionData;
    if (compType === "seo") {
        settingsData.message = labels.pagesManagement.SEO[`message_${page}_page`];
    }

    const component = <div>
        <UpdateController lastEditor={sectionData.last_update ? sectionData.last_update.editor : ''} lastUpdate={formatDate(sectionData.last_update ? sectionData.last_update.time : '')} hasChanges={updateData} update={update}>
            <div className="app-page_management-contaner">
                {pageConfig && <PageComponent data={sectionData} settingsData={settingsData} type={compType} onUpdate={handleUpdateData} validator={validatorRef} />}
            </div>
        </UpdateController>
    </div>;

    return <div className="app-page_management">
        {isLoadingPage && <Loader />}
        {errorLoadingPage && <Dialog onClose={() => { clearErrorMessageLoading(false); }} title={labels.loading_error_title}><p>{labels.loading_error_message}</p></Dialog>}
        {updateError && <Dialog onClose={() => { setUpdateError(false) }} title={localization.labels.save_error_title}><p>{updateError}</p></Dialog>}
        <div className="app-page_data">
            <div className="app-page_data--left">
                <DropDownList
                    data={languagesList}
                    value={languagesList.filter(lang => lang.id == pageLang)[0]}
                    textField="text"
                    dataItemKey="id"
                    onChange={(event) => {
                        if (event.target.value.id !== pageLang) {
                            history.push(`/page/${page}/${event.target.value.id}/${section || ''}`);
                        }
                    }}
                />
            </div>
            <div className="app-page_data--right">
                <div>{labels.menu['page_' + page]}</div>
                <div><a target="_blank" dir="ltr" href={pageUrl}>{pageUrl}</a></div>
            </div>
        </div>
        <div className="app-page_management-content">
            {pageConfig && <TabStrip selected={(pageConfig && pageConfig.seoOnly) ? 0 : contentSelect} onSelect={handleContentSelect} animation={false}>
                {!(pageConfig && pageConfig.seoOnly) && <TabStripTab title={labels.pagesManagement.content}>
                    <TabStrip selected={sectionSelect} onSelect={handleSectionSelect} tabPosition={labels.dir === 'rtl' ? 'right' : 'left'} animation={false}>
                        {pageConfig.content.map((p, i) => {
                            return <TabStripTab
                                key={'section_tab_' + i}
                                title={p.title ? labels?.pagesManagement?.menu_titles && labels.pagesManagement.menu_titles[p.title] :
                                    (page && p.key && labels?.pagesManagement?.menu && labels.pagesManagement.menu[page] ? labels.pagesManagement.menu[page][p.key] : '')}
                                disabled={!p.key}>
                                {contentSelect === 0 && sectionSelect === i && component}
                            </TabStripTab>;
                        })}
                    </TabStrip>
                </TabStripTab>}
                <TabStripTab title={labels.pagesManagement.seo}>
                    {((pageConfig && pageConfig.seoOnly) || contentSelect === 1) && component}
                </TabStripTab>
            </TabStrip>}
        </div>
    </div>;
};
